'use client';

import { useEffect, useRef, useCallback } from 'react';
import { useSession } from 'next-auth/react';
import { signOut } from 'next-auth/react';

export function useTokenRefresh() {
  const { data: session, status, update } = useSession();
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef(false);

  const scheduleTokenRefresh = useCallback(() => {
    if (status !== 'authenticated' || !session?.accessTokenExpires) {
      return;
    }

    if (session.error === 'RefreshAccessTokenError') {
      console.error('Session has refresh error, logging out...');
      signOut({ redirect: true, callbackUrl: '/auth/login' });
      return;
    }

    const expiresAt = session.accessTokenExpires;
    const now = Date.now();
    const timeUntilExpiry = expiresAt - now;
    const refreshBuffer = 60 * 1000;
    const timeUntilRefresh = timeUntilExpiry - refreshBuffer;

    if (timeUntilRefresh > 0) {
      refreshTimeoutRef.current = setTimeout(async () => {
        if (isRefreshingRef.current) {
          console.log('Refresh already in progress, skipping...');
          return;
        }

        isRefreshingRef.current = true;
        console.log('Proactively refreshing access token...');

        try {
          await update();
          console.log('Token refresh triggered successfully');
        } catch (error) {
          console.error('Failed to refresh token:', error);
          signOut({ redirect: true, callbackUrl: '/auth/login' });
        } finally {
          isRefreshingRef.current = false;
        }
      }, timeUntilRefresh);
    } else if (!isRefreshingRef.current) {
      isRefreshingRef.current = true;
      update()
        .catch((error) => {
          console.error('Failed to refresh token:', error);
          signOut({ redirect: true, callbackUrl: '/auth/login' });
        })
        .finally(() => {
          // Prevent a tight update() loop when the session cookie cannot be decrypted
          setTimeout(() => {
            isRefreshingRef.current = false;
          }, 15_000);
        });
    }
  }, [session, status, update]);

  useEffect(() => {
    scheduleTokenRefresh();

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [scheduleTokenRefresh]);

  return null;
}
